// Project portfolio shown on /about. Content moved verbatim out of Resume.tsx
// so the component renders data instead of carrying 13 hand-duplicated cards.
// Order: most recent first. `featured` rows render expanded by default.

export const ProjectsData = [
    {
        id: 'hybrid-rag',
        featured: true,
        title: 'Hybrid RAG System with AWS Bedrock',
        subtitle: '2024 • Enterprise-scale AI System',
        overview: 'Enterprise-scale AI system for intelligent document processing and retrieval. Architected a hybrid-cloud RAG (Retrieval-Augmented Generation) system that significantly improved query response times and accuracy across multiple data sources.',
        keyFeatures: [
            'Hybrid-Cloud Architecture - Seamlessly integrated on-premise and cloud-based data sources',
            'AWS Bedrock Integration - Leveraged advanced LLM capabilities for enhanced document understanding',
            'Intelligent Document Processing - Automated extraction and indexing of diverse document formats',
            'Multi-Source Retrieval - Unified access to distributed knowledge repositories',
            'Real-time Query Processing - Optimized pipeline for sub-second response times'
        ],
        technicalHighlights: [
            'Custom RAG pipeline with vector embeddings and semantic search',
            'AWS Bedrock model integration and fine-tuning',
            'Distributed data processing with Apache Spark',
            'Real-time indexing and document ingestion pipeline',
            'Advanced caching mechanisms for improved performance'
        ],
        technologies: ['AWS Bedrock', 'Python', 'TensorFlow', 'GCP', 'Apache Spark', 'Vector Databases', 'Docker', 'Kubernetes'],
        achievements: [
            'Reduced query response time by 60%',
            'Improved document retrieval accuracy by 40%',
            'Processed 10TB+ of enterprise documents',
            'Achieved 99.5% system uptime across hybrid infrastructure'
        ]
    },
    {
        id: 'n8n',
        featured: false,
        title: 'n8n Workflow Automation Platform',
        subtitle: '2024-Current • Probabilistic AI Agent Workflows',
        overview: 'Advanced workflow automation platform designed for enterprise-scale data cleansing and processing. Built sophisticated probabilistic flow systems that leverage AI agents for intelligent decision-making and data transformation.',
        keyFeatures: [
            'Probabilistic Flow Engine - Built custom probability-based routing system for data processing workflows',
            'AI Agent Integration - Deployed multiple AI agents for different data validation and transformation tasks',
            'Real-time Data Cleansing - Automated data quality checks with instant correction and validation',
            'Smart Error Handling - Intelligent error detection and automatic retry mechanisms',
            'Scalable Processing - Designed to handle large-scale data operations with parallel processing capabilities'
        ],
        technicalHighlights: [
            'Custom n8n nodes for probabilistic decision making',
            'Integration with multiple AI/ML APIs for data analysis',
            'Real-time monitoring and alerting system',
            'RESTful API endpoints for external system integration',
            'Automated workflow versioning and rollback capabilities'
        ],
        technologies: ['n8n', 'Node.js', 'JavaScript', 'AI/ML APIs', 'REST APIs', 'Docker', 'MongoDB', 'Redis'],
        achievements: [
            '90% reduction in manual data processing time',
            'Improved data quality accuracy by 85%',
            'Automated 70+ repetitive data workflows',
            'Reduced operational costs by 60%'
        ]
    },
    {
        id: 'llm-finetuning',
        featured: false,
        title: 'LLM Fine-tuning & Model Steering Research',
        subtitle: '2024-Current • Advanced AI Model Optimization',
        overview: 'Cutting-edge research and implementation of Large Language Model optimization techniques. Focus on LoRA (Low-Rank Adaptation) for efficient fine-tuning and advanced model steering methodologies for improved AI behavior alignment.',
        keyFeatures: [
            'LoRA Implementation - Efficient fine-tuning with 99% parameter reduction while maintaining performance',
            'Model Steering Research - Advanced techniques for controlling AI model behavior and response patterns',
            'Custom Training Pipelines - Built automated training workflows for various model architectures',
            'Performance Optimization - Memory-efficient training methods for large-scale model deployment',
            'Behavior Analysis Tools - Comprehensive evaluation frameworks for model performance assessment'
        ],
        technicalHighlights: [
            'Custom LoRA adapters for domain-specific fine-tuning',
            'Advanced gradient manipulation techniques',
            'Distributed training across multiple GPUs',
            'Custom loss functions for behavior steering',
            'Automated hyperparameter optimization'
        ],
        technologies: ['PyTorch', 'HuggingFace Transformers', 'LoRA', 'Python', 'CUDA', 'Weights & Biases', 'Docker', 'Kubernetes'],
        achievements: [
            '75% reduction in fine-tuning computational costs',
            'Improved model alignment scores by 40%',
            'Successfully fine-tuned 15+ different model architectures',
            'Published 3 research papers on model steering techniques'
        ]
    },
    {
        id: 'gcp-chatbot',
        featured: false,
        title: 'GCP Workspace RAG Chatbot Integration',
        subtitle: '2023-2024 • AI Knowledge Assistant',
        overview: 'Enterprise-grade Retrieval-Augmented Generation (RAG) chatbot integrated with Google Cloud Platform Workspace. Leverages Amazon Bedrock knowledge base for intelligent document retrieval and contextual responses across organizational data sources.',
        keyFeatures: [
            'Workspace Integration - Seamless integration with Google Workspace suite (Gmail, Drive, Docs, Sheets)',
            'RAG Architecture - Advanced retrieval system for accurate, context-aware responses',
            'Multi-source Data Access - Unified access to various organizational knowledge repositories',
            'Real-time Collaboration - Live assistance during meetings and document editing',
            'Enterprise Security - Full compliance with organizational security and privacy policies'
        ],
        technicalHighlights: [
            'Custom Google Workspace add-on architecture',
            'Amazon Bedrock knowledge base integration',
            'Vector embeddings for semantic search',
            'Real-time document processing pipeline',
            'Advanced caching mechanisms for improved performance'
        ],
        technologies: ['Google Cloud Platform', 'Amazon Bedrock', 'Python', 'Google Apps Script', 'Vector Databases', 'OpenAI API', 'Docker', 'Kubernetes'],
        achievements: [
            '60% faster information retrieval times',
            'Improved employee productivity by 40%',
            'Deployed to 500+ users across organization',
            '95% user satisfaction rating'
        ]
    },
    {
        id: 'agentforce',
        featured: true,
        title: 'Salesforce Agentforce Automation Platform',
        subtitle: '2023-2024 • Multi-language Email Automation',
        overview: 'Advanced AI-powered automation platform built on Salesforce Agentforce. Features comprehensive email translation, sentiment analysis, and intelligent routing capabilities for global customer service operations.',
        keyFeatures: [
            'Multi-language Translation - Real-time translation of incoming and outgoing emails across 25+ languages',
            'Sentiment Analysis Engine - Advanced AI-powered sentiment detection with confidence scoring',
            'Flex Prompt Templates - Dynamic AI response generation based on context and customer history',
            'Intelligent Routing - Smart case assignment based on complexity, language, and agent expertise',
            'Automated Workflows - End-to-end automation of customer service processes'
        ],
        technicalHighlights: [
            'Custom Agentforce action implementations',
            'Integration with translation APIs',
            'Real-time sentiment analysis pipeline',
            'Advanced prompt engineering techniques',
            'Custom Lightning Web Components for admin interface'
        ],
        technologies: ['Salesforce Agentforce', 'Apex', 'LWC', 'Einstein AI', 'Translation APIs', 'REST APIs', 'SOQL', 'Lightning Platform'],
        achievements: [
            'Reduced response time from hours to minutes',
            'Improved customer satisfaction by 45%',
            'Automated 80% of routine customer inquiries',
            'Supported 25+ languages with 95% accuracy'
        ]
    },
    {
        id: 'einstein-chatbot',
        featured: false,
        title: 'Einstein Chat Bot for Experience Cloud',
        subtitle: 'Jul 2023 - Sep 2023 • Zalando SE',
        overview: 'Designed and developed an advanced Einstein Bot for Experience Cloud with intelligent features including article search and automated intent generation. Built custom chat window implementation and comprehensive reporting capabilities.',
        keyFeatures: [
            'Smart Article Search - Intelligent search functionality to find relevant knowledge articles',
            'Intent Generation - Automated intent detection and response generation',
            'Custom Chat Window - Built tailored chat interface for Experience Cloud integration',
            'Reporting Features - Comprehensive analytics and conversation tracking',
            'Experience Cloud Integration - Seamless integration with Salesforce Experience Cloud'
        ],
        technicalHighlights: [
            'Custom Einstein Bot dialog flows and intents',
            'Experience Cloud component development',
            'Knowledge article integration and search optimization',
            'Custom chat UI/UX implementation',
            'Analytics and reporting dashboard creation'
        ],
        technologies: ['Einstein Analytics', 'Experience Cloud', 'Salesforce', 'Apex', 'Lightning Web Components', 'SOQL'],
        achievements: [
            'Improved customer self-service resolution by 40%',
            'Reduced support ticket volume by 30%',
            'Implemented across multiple Experience Cloud sites',
            'Enhanced user engagement with intelligent responses'
        ]
    },
    {
        id: 'campaign-portal',
        featured: false,
        title: 'Global Campaign Portal Design',
        subtitle: 'May 2019 - Jan 2020 • Leading Sports Brand (Infosys)',
        overview: 'Built comprehensive campaign management portal with multi-level KPI calculations and advanced analytics. Developed 30+ Lightning components with timeline views and integrated 3rd party systems for seamless business flow.',
        keyFeatures: [
            'Multi-level KPI Calculations - Complex performance metrics across campaign hierarchies',
            'Timeline Views - Visual campaign timeline management and tracking',
            '30+ Lightning Components - Custom components for campaign management workflows',
            '3rd Party Integrations - Seamless integration with external marketing systems',
            'Advanced Analytics - Comprehensive reporting and data visualization'
        ],
        technicalHighlights: [
            'Complex Apex batch processing for KPI calculations',
            'Custom Lightning Web Components architecture',
            'Real-time data synchronization with external systems',
            'Advanced SOQL optimization for large datasets',
            'Custom reporting and dashboard frameworks'
        ],
        technologies: ['Lightning Components', 'Apex', 'KPI Analytics', 'Batch Jobs', 'SOQL', 'Lightning Web Components', 'Salesforce Integration'],
        achievements: [
            'Managed campaigns worth $50M+ in marketing spend',
            'Reduced campaign setup time by 60%',
            'Improved KPI calculation accuracy by 85%',
            'Streamlined workflow for 200+ marketing users'
        ]
    },
    {
        id: 'cicd-pipeline',
        featured: false,
        title: 'CI/CD Pipeline Management',
        subtitle: 'Jan 2019 - Apr 2019 • LemonOne',
        overview: 'Designed and implemented comprehensive automated CI/CD pipeline with Jenkins integration, Bitbucket automation, and code quality assurance processes using PMD for enhanced development workflows.',
        keyFeatures: [
            'Automated CI/CD Pipeline - End-to-end automation from code commit to deployment',
            'Jenkins Integration - Robust build and deployment automation',
            'Bitbucket Automation - Automated code review and merge processes',
            'Code Quality Gates - PMD integration for code quality enforcement',
            'Deployment Orchestration - Automated multi-environment deployment management'
        ],
        technicalHighlights: [
            'Jenkins pipeline scripting and job configuration',
            'Bitbucket webhook integration and automation',
            'PMD rule customization and code quality metrics',
            'Automated testing integration and reporting',
            'Multi-environment deployment strategies'
        ],
        technologies: ['Jenkins', 'Bitbucket', 'PMD', 'DevOps', 'Shell Scripting', 'Git', 'Automated Testing'],
        achievements: [
            'Reduced deployment time from hours to minutes',
            'Improved code quality scores by 70%',
            'Automated 90% of manual deployment processes',
            'Zero-downtime deployment implementation'
        ]
    },
    {
        id: 'support-process',
        featured: false,
        title: 'Global Campaign Support Process Design',
        subtitle: 'Feb 2019 - Apr 2019 • Adidas',
        overview: 'Designed comprehensive 3-tier support structure (L1, L2, L3) with ServiceNow integration, advanced error monitoring, and proactive issue identification for campaign portal management system.',
        keyFeatures: [
            '3-Tier Support Structure - Comprehensive L1, L2, L3 support hierarchy',
            'ServiceNow Integration - Seamless ticketing and case management',
            'Error Monitoring - Real-time system health and error tracking',
            'Proactive Issue Identification - Automated issue detection and alerting',
            'Support Process Documentation - Comprehensive operational procedures'
        ],
        technicalHighlights: [
            'ServiceNow workflow configuration and customization',
            'Real-time monitoring and alerting systems',
            'Automated escalation procedures and SLA management',
            'Integration with Salesforce monitoring tools',
            'Custom reporting and analytics dashboards'
        ],
        technologies: ['3-Tier Support', 'Service Now', 'Reports & Dashboards', 'Monitoring Tools', 'SLA Management'],
        achievements: [
            'Reduced average resolution time by 50%',
            'Improved first-call resolution rate to 80%',
            'Implemented proactive monitoring preventing 60% of issues',
            'Established 24/7 global support coverage'
        ]
    },
    {
        id: 'react-integration',
        featured: false,
        title: 'Campaign Portal with React Integration',
        subtitle: 'Aug 2018 - Dec 2018 • Adidas',
        overview: 'Successfully transitioned from traditional systems to modern Salesforce-based solution. Developed React-based calendar integration with Lightning components and implemented complex Excel-based functionalities within Salesforce.',
        keyFeatures: [
            'React-Lightning Integration - Seamless React component integration with Lightning',
            'Advanced Calendar System - Interactive calendar with campaign scheduling',
            'Excel Functionality Migration - Complex spreadsheet features ported to Salesforce',
            'System Migration - Complete transition from legacy systems',
            'Hybrid Architecture - Modern frontend with Salesforce backend integration'
        ],
        technicalHighlights: [
            'React component development and Lightning integration',
            'Custom calendar component with advanced scheduling features',
            'Complex data migration and transformation processes',
            'Lightning Web Component architecture implementation',
            'Advanced JavaScript and React state management'
        ],
        technologies: ['React', 'Lightning Components', 'Calendar Integration', 'JavaScript', 'Apex', 'Lightning Web Components'],
        achievements: [
            'Successfully migrated 100% of legacy functionality',
            'Improved user experience ratings by 90%',
            'Reduced system maintenance overhead by 40%',
            'Enhanced calendar functionality with 50+ new features'
        ]
    },
    {
        id: 'consumer-service',
        featured: false,
        title: 'Consumer Service Operations Leadership',
        subtitle: 'Jan 2017 - Jul 2018 • Leading Sports Brand (Infosys)',
        overview: 'Led CRM operations team for leading sports brand, working on world\'s first Salesforce-based consumer service chatbot, managing production deployments, and integrating mobile applications with Salesforce.',
        keyFeatures: [
            'World\'s First Salesforce Chatbot - Pioneer implementation of Salesforce-based customer service bot',
            'Team Leadership - Led cross-functional CRM operations team',
            'Production Deployment Management - Oversaw critical system deployments',
            'Mobile Integration - Seamless mobile app integration with Salesforce CRM',
            'Wave Analytics Implementation - Advanced analytics and reporting solutions'
        ],
        technicalHighlights: [
            'Chatbot framework development and implementation',
            'Production deployment orchestration and monitoring',
            'Mobile API development and integration',
            'Wave Analytics dashboard and report creation',
            'Team management and technical leadership'
        ],
        technologies: ['Team Leadership', 'Production Operations', 'Chatbot (World\'s First)', 'Wave Analytics', 'Mobile Integration', 'Salesforce CRM'],
        achievements: [
            'Successfully launched world\'s first Salesforce-based customer service chatbot',
            'Managed team of 15+ technical professionals',
            'Achieved 99.9% system uptime across all deployments',
            'Integrated mobile apps serving 1M+ active users'
        ]
    },
    {
        id: 'supply-chain',
        featured: false,
        title: 'Supply Chain Management Portal',
        subtitle: 'May 2015 - Jul 2015 • Chinese Dairy Company (Infosys)',
        overview: 'Drove development and Go-Live rollouts for comprehensive supplier chain management portal. Implemented single sign-on authentication, enhanced supplier loyalty point system, and managed complex system migration from local vendor systems.',
        keyFeatures: [
            'Supplier Chain Management - Complete supplier lifecycle management portal',
            'Single Sign-On Implementation - Seamless authentication across systems',
            'Loyalty Point System - Enhanced supplier rewards and engagement program',
            'System Migration - Complex migration from legacy vendor systems',
            'Go-Live Rollout Management - Comprehensive deployment and change management'
        ],
        technicalHighlights: [
            'Service Cloud configuration and customization',
            'SSO implementation and security architecture',
            'Complex data migration and transformation',
            'Loyalty points calculation engine development',
            'Integration with multiple supplier systems'
        ],
        technologies: ['Service Cloud', 'Single Sign-On', 'Supplier Loyalty System', 'Data Migration', 'System Integration'],
        achievements: [
            'Successfully migrated 500+ supplier accounts',
            'Implemented SSO reducing login issues by 95%',
            'Launched loyalty program increasing supplier engagement by 60%',
            'Completed Go-Live rollout on schedule with zero critical issues'
        ]
    },
    {
        id: 'database-migration',
        featured: false,
        title: 'Database Administration & Migration',
        subtitle: 'Jan 2015 - Mar 2015 • Infosys',
        overview: 'Led comprehensive technical transition from traditional RDBMS to MongoDB. Successfully migrated entire system data, developed automation batches for seamless database migration, and created integrated systems for migration automation.',
        keyFeatures: [
            'RDBMS to MongoDB Migration - Complete database platform transition',
            'Data Migration Automation - Automated migration processes and validation',
            'System Integration - Integrated migration tools and monitoring systems',
            'Batch Processing Development - Custom automation for large-scale data migration',
            'Migration Validation - Comprehensive data integrity and validation processes'
        ],
        technicalHighlights: [
            'Database schema design and optimization for MongoDB',
            'Complex data transformation and migration scripting',
            'Automation batch development and scheduling',
            'Data validation and integrity checking systems',
            'Performance optimization for large-scale migrations'
        ],
        technologies: ['Oracle', 'MongoDB', 'Database Migration', 'Automation', 'Data Transformation', 'Batch Processing'],
        achievements: [
            'Successfully migrated 10TB+ of critical business data',
            'Achieved 99.99% data integrity during migration',
            'Reduced migration time by 80% through automation',
            'Zero data loss during complete system transition'
        ]
    },
];
